import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { Maybe } from 'graphql/jsutils/Maybe';
import { twMerge } from 'tailwind-merge';
import { faCheck } from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import { Button } from '@components/main-components/button/Button';
import { NextFutureImage } from '@components/main-components/image/NextFutureImage';
import { DataTestId } from '@utils/test';

type Props = {
  isSaved: Maybe<boolean>;
  isLoading: boolean;
  onClick: () => void;
  spotifyLink: string | undefined;
};

export function MobileHeroVariant({ isSaved, isLoading, onClick, spotifyLink }: Props) {
  const labelComponent = useMemo(() => {
    return !isLoading && !isSaved ? (
      <div className="text-left">
        <p className="text-[12px]/[18px] font-medium">Presave</p>
        <p className="text-[10px]/[12px] opacity-80">to claim</p>
      </div>
    ) : null;
  }, [isLoading, isSaved]);

  return (
    <Button
      href={spotifyLink}
      testId={DataTestId.SPOTIFY_PRESAVE_BUTTON}
      className={twMerge(
        'ml-auto min-w-[80px] max-w-none font-title md:ml-[unset]',
        isSaved
          ? 'cursor-default bg-transparent text-white hover:border-white hover:bg-transparent hover:text-white'
          : 'text-black',
      )}
      variant="tertiary"
      label={isSaved ? 'Presaved' : 'Presave to claim'}
      labelComponent={labelComponent}
      leadingIcon={
        isSaved ? (
          <FontAwesomeIcon icon={faCheck} fontSize={20} />
        ) : (
          <NextFutureImage
            alt="spotify logo"
            src="/images/spotify_logo_green.png"
            width={22}
            height={22}
          />
        )
      }
      loading={isLoading}
      onClick={onClick}
    />
  );
}

export function HeroVariant({ isSaved, isLoading, onClick, spotifyLink }: Props) {
  const labelComponent = useMemo(() => {
    return !isLoading && !isSaved ? (
      <div className="text-left">
        <p className="text-[12px]/[18px] font-medium">Presave to Spotify</p>
        <p className="text-[10px]/[12px] opacity-80">Claim Free Edition</p>
      </div>
    ) : null;
  }, [isLoading, isSaved]);

  return (
    <Button
      href={spotifyLink}
      testId={DataTestId.SPOTIFY_PRESAVE_BUTTON}
      className={twMerge(
        'ml-auto min-w-[164px] max-w-none font-title md:ml-[unset]',
        isSaved
          ? 'cursor-default bg-transparent text-white hover:border-white hover:bg-transparent hover:text-white'
          : 'text-black',
      )}
      variant="tertiary"
      label={isSaved ? 'Presaved' : 'Presave to claim'}
      labelComponent={labelComponent}
      leadingIcon={
        isSaved ? (
          <FontAwesomeIcon icon={faCheck} fontSize={20} />
        ) : (
          <NextFutureImage
            alt="spotify logo"
            src="/images/spotify_logo_green.png"
            width={22}
            height={22}
          />
        )
      }
      loading={isLoading}
      onClick={onClick}
    />
  );
}
